import { useLanguage, useMessageActionContext } from '@/app/providers';
import { StarIcon } from '@/assets/images';
import { Checkbox, CustomButton } from '@/components';
import MessageActionDropdown from '@/features/chats/ui/Conversation/components/MessageActionDropdown';
import { isGroupChat } from '@/features/chats/utils/jidIdFormat';
import { useMessageAction } from '@/features/chats/viewmodels/useMessageAction';
import { formatChatTime } from '@/features/timeformatter/timeStamp';
import { Username, UserProfile } from '@/features/users';
import { VITE_GOOGLE_LOCATION_API_KEY } from '@/processENV';
import { getLocalUserId, getMessageSenderId } from '@/shared/utils';
import PropTypes from 'prop-types';
import { memo, useMemo } from 'react';
import MessageStatus from '../MessageStatus';
import ReplyMessageCard from '../ReplyMessageCard';

const MAP_URL = 'https://maps.googleapis.com/maps/api/staticmap';

const SENDER_CLASS = 'sender-chat ct-rowReverse chat-bubble ct-flex ct-aiEnd ct-gap10 ct-padding-lr8-rl16';
const RECEIVER_CLASS = 'receiver-chat chat-bubble ct-flex ct-gap10 ct-padding-lr8-rl16';
const SENDER_INNER = 'ct-flex ct-relative ct-flexColEnd ct-gap10';
const RECEIVER_INNER = 'ct-flex ct-relative ct-flexCol ct-gap10';

const LocationBox = ({ message, onMediaClick, activeChatJid, showProfile, senderId }) => {
   const locationObj = message.msgBody?.location || {};
   const latitude = locationObj.latitude;
   const longitude = locationObj.longitude;
   const { t } = useLanguage();
   const title = t('common.PINNED_LOCATION');
   const addressText = locationObj.address;

   // Usually standard thumb_image or mapURL generated
   const mapImage = message.msgBody?.media?.file_url || message.msgBody?.media?.thumb_image || '';

   const staticMapUrl = useMemo(() => {
      if (latitude && longitude && VITE_GOOGLE_LOCATION_API_KEY) {
         return `${MAP_URL}?center=${latitude},${longitude}&zoom=13&size=300x180&markers=color:red|${latitude},${longitude}&key=${VITE_GOOGLE_LOCATION_API_KEY}`;
      }
      return mapImage || `https://via.placeholder.com/300x180.png?text=${t('common.MAP_LOCATION').replace(/ /g, '+')}`;
   }, [latitude, longitude, mapImage, t]);

   return (
      <div className="chat-box ct-flex ct-flexCol" style={{ padding: 0, overflow: 'hidden', maxWidth: '18.75rem' }}>
         {showProfile && <Username userId={senderId} />}
         <ReplyMessageCard replyTo={message.msgBody?.replyTo} activeChatJid={activeChatJid} />
         <a
            className="message-location"
            target="_blank"
            rel="noopener noreferrer"
            href={`https://www.google.com/maps/?q=${latitude},${longitude}`}
            style={{ display: 'block', position: 'relative' }}
         >
            <CustomButton
               as="div"
               onClick={e => {
                  e.stopPropagation();
                  onMediaClick?.(e);
               }}
               className="ct-cursor ct-relative ct-block"
               aria-label={t('common.MAP_LOCATION')}
            >
               <img
                  src={staticMapUrl}
                  alt={t('common.MAP_LOCATION')}
                  style={{ width: '100%', height: '11.25rem', objectFit: 'cover', display: 'block' }}
               />
            </CustomButton>
         </a>
         <div className="ct-flex ct-flexCol location-content">
            <div className="pinned-location">{title}</div>
            <div className="location-address">{addressText}</div>
         </div>
      </div>
   );
};

LocationBox.propTypes = {
   message: PropTypes.object.isRequired,
   onMediaClick: PropTypes.func.isRequired,
   activeChatJid: PropTypes.string,
   showProfile: PropTypes.bool,
   senderId: PropTypes.string,
};

const LocationCard = memo(({ message, activeChatJid }) => {
   const { t } = useLanguage();
   const { hideAvatar = false, hideActions = false } = useMessageActionContext();
   const { isSelected, isSelectionActive, handleToggleSelection } = useMessageAction(message, activeChatJid);

   const senderId = getMessageSenderId({ type: 'chats', message });
   const isSender = senderId === getLocalUserId();
   const timestamp = useMemo(
      () => (message.timestamp ? formatChatTime(new Date(Number(message.timestamp)), 'message-card') : ''),
      [message.timestamp],
   );
   const hideAvatarLocationCard = isSender || hideAvatar;
   const isChatGroup = useMemo(() => isGroupChat(activeChatJid), [activeChatJid]);
   const isHighlighted = message?.isHighlighted;
   const showProfile = isChatGroup && !hideAvatarLocationCard;

   return (
      <CustomButton
         as="div"
         className={`message-row custom-location-card ${isSelectionActive && !hideActions ? 'cts-location ct-flex ct-aiCenter selection-active' : ''} ${isSelected && !hideActions ? 'selected' : ''} ${isHighlighted && !hideActions ? 'search-highlight-active' : ''}`}
         onClick={isSelectionActive && !hideActions ? handleToggleSelection : null}
      >
         {isSelectionActive && !hideActions && (
            <div className="selection-checkbox-wrap cts-location">
               <Checkbox checked={isSelected} onChange={handleToggleSelection} />
            </div>
         )}
         <div className={`${isSender ? SENDER_CLASS : RECEIVER_CLASS}`}>
            {showProfile && (
               <picture className="chat-avatar cts-location">
                  <UserProfile userId={senderId} className="chat-avatar chat-avatar-fallback ct-flexCenter" />
               </picture>
            )}
            <div className={isSender ? SENDER_INNER : RECEIVER_INNER}>
               <LocationBox
                  message={message}
                  activeChatJid={activeChatJid}
                  showProfile={showProfile}
                  senderId={senderId}
               />
               <div className={`cts-location ct-flexAlign ct-flex ct-gap8 ${isSender ? 'ct-rowReverse' : ''}`}>
                  {isSender && <MessageStatus msgStatus={message.msgStatus} />}
                  <span className="time cts-location">{timestamp}</span>
                  {message.editedStatus === 1 && <span className="edited-icon">{t('message.EDITED')}</span>}
                  {message.favouriteStatus === 1 && (
                     <i className="icon starIcon ct-flexCenter">
                        <StarIcon />
                     </i>
                  )}
               </div>
               {!hideActions && (
                  <MessageActionDropdown
                     className="cts-location"
                     message={message}
                     isSender={isSender}
                     activeChatJid={activeChatJid}
                  />
               )}
            </div>
         </div>
      </CustomButton>
   );
});

LocationCard.displayName = 'LocationCard';
LocationCard.propTypes = {
   message: PropTypes.object.isRequired,
   activeChatJid: PropTypes.string,
};

export default LocationCard;
